import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import { useScope } from "hooks/useScope";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useLazyGetCompaniesQuery,
  useLazyGetOrganizationsQuery,
} from "reduxFiles/api/companyApi";
import {
  useCreateLicenseMutation,
  useGetLicensesQuery,
} from "reduxFiles/api/licenseApi";
import { useLazyGetUsersQuery } from "reduxFiles/api/userApi";
import { enumToOptions } from "services/helper";
import {
  GroupType,
  LicenseDTO,
  LicenseScope,
  LicenseTier,
} from "types/assessmentTypes";
import { Column, TableAction } from "types/globalTypes";

const AdminLicenses = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scope = useScope();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetLicensesQuery({
    scope,
    page: pagination.page,
    limit: pagination.limit,
    filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateLicenseMutation();

  // users
  const [triggerUsers] = useLazyGetUsersQuery();
  const searchUsers = async (text: string) => {
    try {
      const res = await triggerUsers({
        page: 1,
        limit: 50,
        filters: { search: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.fullname + " " + u.mobile || "-",
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // search organizations
  const [triggerOrganizations] = useLazyGetOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // search companies
  const [triggerCompanies] = useLazyGetCompaniesQuery();
  const searchCompanies = async (text: string) => {
    try {
      const res = await triggerCompanies({
        scope: "admin",
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form
  const fields: Field[] = [
    {
      name: "scope",
      label: t("licenses.scope"),
      type: "select",
      options: enumToOptions(LicenseScope, t, "licenses.scopes"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "tier",
      label: t("licenses.tier"),
      type: "select",
      options: enumToOptions(LicenseTier, t, "licenses.tiers"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "userId",
      label: t("licenses.userId"),
      type: "select",
      onSearch: searchUsers,
      formed: true,
      required: true,
      showWhen: {
        field: "scope",
        equals: LicenseScope.COMPANY,
      },
    },
    {
      name: "userId",
      label: t("licenses.userId"),
      type: "select",
      onSearch: searchUsers,
      filtered: true,
    },
    {
      name: "groupType",
      label: t("licenses.groupType"),
      type: "select",
      options: enumToOptions(GroupType, t, "group.types"),
      filtered: true,
      formed: true,
      required: true,
      showWhen: {
        field: "scope",
        equals: LicenseScope.COMPANY,
      },
    },
    {
      name: "companyId",
      label: t("licenses.companies"),
      type: "select",
      onSearch: searchCompanies,
      filtered: true,
    },
    {
      name: "organizationId",
      label: t("licenses.organizations"),
      type: "select",
      onSearch: searchOrganizations,
      formed: true,
      required: true,
      filtered: true,
      showWhen: {
        field: "scope",
        equals: LicenseScope.ORGANIZATION,
      },
    },
    {
      name: "organizationId",
      label: t("licenses.organizations"),
      type: "select",
      onSearch: searchOrganizations,
      filtered: true,
    },
    {
      name: "quantity",
      label: t("licenses.quantity"),
      type: "number",
      formed: true,
      required: true,
    },
    {
      name: "used",
      label: t("licenses.status"),
      type: "select",
      options: [
        { label: t("licenses.unused"), value: "false" },
        { label: t("licenses.used"), value: "true" },
      ],
      filtered: true,
    },
    {
      name: "licenseKey",
      label: t("licenses.licenseKey"),
      type: "text",
      filtered: true,
    },
    {
      name: "note",
      label: t("licenses.note"),
      type: "textarea",
      formed: true,
    },
  ];

  const columns: Column<LicenseDTO>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "scope",
      title: t("licenses.scope"),
      render: (row) => t(`licenses.scopes.${row.scope}`),
    },
    {
      key: "user",
      title: t("licenses.model"),
      render: (row) =>
        row.scope === LicenseScope.ORGANIZATION
          ? row.organization?.name + " " + row.organization?.province?.name
          : row.user?.fullname ?? "",
    },
    {
      key: "tier",
      title: t("licenses.tier"),
      render: (row) => t(`licenses.tiers.${row.tier}`),
    },
    {
      key: "licenseKey",
      title: t("licenses.licenseKey"),
    },
    {
      key: "groupType",
      title: t("licenses.groupType"),
      render: (row) =>
        row.groupType ? t(`group.types.${row.groupType}`) : "-",
    },
    {
      key: "usedAt",
      title: t("licenses.usedAt"),
      render: (row) =>
        row.used
          ? moment(row.usedAt).format(
              lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
            )
          : "-",
    },
    {
      key: "company",
      title: t("licenses.companies"),
      render: (row) => row.company?.name || "-",
    },
  ];

  const actions: TableAction<LicenseDTO>[] = [];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (data: FieldValues) => {
    try {
      const body = {
        scope: data.scope,
        tier: data.tier,
        organizationId:
          data.scope === LicenseScope.COMPANY ? undefined : data.organizationId,
        userId: data.scope === LicenseScope.COMPANY ? data.userId : undefined,
        groupType:
          data.scope === LicenseScope.COMPANY ? data.groupType : undefined,
        quantity: data.quantity,
        note: data.note,
      };
      await addApi(body).unwrap();
      modal.closeModal();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("licenses.myLicensesList")}
          createBtn={{
            title: t("licenses.createLicense"),
            handler: () => {
              modal.openModal();
            },
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<LicenseDTO>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => modal.closeModal()}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={{}}
          title={t("licenses.createLicense")}
          isEdit={false}
          loading={isAdding}
        />
      </div>
    </div>
  );
};

export default AdminLicenses;
